import { Controller } from "@hotwired/stimulus"

// Keeps a bottom-docked bar flush with the on-screen keyboard. iOS shrinks
// the visual viewport when the keyboard opens but leaves fixed elements
// pinned to the layout viewport, so the bar would sink behind the keys; the
// visual viewport's bottom edge (offsetTop + height) says exactly where the
// visible area ends, and the bar travels up to sit on it — no margins, no
// safe-area, keyboard-flush like a native accessory view. On browsers that
// resize the layout viewport instead (Chrome on Android with
// interactive-widget=resizes-content), the shift is zero and the bar stays
// where CSS put it. The data-keyboard-open attribute lets CSS drop the
// safe-area padding while the keyboard covers that zone anyway.
export default class extends Controller {
  connect() {
    this.viewport = window.visualViewport
    if (!this.viewport) return

    // The page scrolls the caret into view against the visual viewport;
    // reserving the bar's height keeps the caret above it, not behind it.
    document.documentElement.style.scrollPaddingBottom = `${this.element.offsetHeight}px`

    this.reposition = this.reposition.bind(this)
    this.viewport.addEventListener("resize", this.reposition)
    this.viewport.addEventListener("scroll", this.reposition)
    this.reposition()
  }

  disconnect() {
    if (!this.viewport) return

    document.documentElement.style.scrollPaddingBottom = ""
    this.viewport.removeEventListener("resize", this.reposition)
    this.viewport.removeEventListener("scroll", this.reposition)
  }

  reposition() {
    // The bar rests at bottom: 0, i.e. at window.innerHeight in layout
    // coordinates — the same coordinate space as the visual viewport's
    // offsets. A URL bar collapse shifts a few pixels; only a real
    // keyboard opens a gap worth undocking the safe-area padding for.
    const visualBottom = this.viewport.offsetTop + this.viewport.height
    const shift = Math.min(0, Math.round(visualBottom - window.innerHeight))

    this.element.toggleAttribute("data-keyboard-open", shift < -100)
    this.element.style.transform = shift ? `translateY(${shift}px)` : ""
  }
}
